import type { Content, Element, Root, Text } from "hast";
import type { Plugin } from "unified";
import { EXIT, SKIP, visit } from "unist-util-visit";

const SKIP_TAGS = new Set(["code", "pre", "abbr"]);

export function rehypeTtsHighlight(
  activeIdx: number | null,
  activeLen: number | null
): Plugin<[], Root> {
  return function () {
    return function (tree: Root) {
      if (activeIdx == null || activeLen == null || activeLen <= 0) return;

      let offset = 0;
      let done = false;

      visit(tree, (node, index, parent) => {
        if (done) return EXIT;
        if (!parent) return;

        if ((node as Element).type === "element") {
          const el = node as Element;
          if (el.tagName && SKIP_TAGS.has(el.tagName)) {
            offset += getTextLength(el);
            return SKIP;
          }
        }

        if ((node as Text).type === "text") {
          const textNode = node as Text;
          const len = textNode.value.length;
          const start = offset;
          const end = offset + len;

          const a0 = activeIdx as number;
          const a1 = (activeIdx as number) + (activeLen as number);

          const overlapStart = Math.max(start, a0);
          const overlapEnd = Math.min(end, a1);

          if (
            overlapStart < overlapEnd &&
            typeof index === "number" &&
            parent
          ) {
            const before = textNode.value.slice(0, overlapStart - start);
            const middle = textNode.value.slice(
              overlapStart - start,
              overlapEnd - start
            );
            const after = textNode.value.slice(overlapEnd - start);

            const nodes: Content[] = [];
            if (before) nodes.push({ type: "text", value: before });
            nodes.push({
              type: "element",
              tagName: "span",
              properties: { className: ["underline", "decoration-dotted"] },
              children: [{ type: "text", value: middle }],
            });
            if (after) nodes.push({ type: "text", value: after });

            parent.children.splice(index, 1, ...nodes);
            done = true;
            return EXIT;
          }

          offset = end;
        }
      });

      function getTextLength(el: Element): number {
        let total = 0;
        visit(el, (n) => {
          if ((n as Text).type === "text") {
            total += (n as Text).value.length;
          }
        });
        return total;
      }
    };
  };
}
